import { Injectable } from '@angular/core';
import { OwnUserProfile, parseAccountLanguage } from '@app/shared/types/models/user/user-profile.model';
import { UserProfileDTO } from '../dtos/users/user-profile.dto';
import { AbstractFactory } from './abstract.factory';

/**
 * Converts the flat `GET /api/v3/users/me/profile` response into {@link OwnUserProfile}.
 * Validates required fields/id/enum; only the three documented nullable text fields may normalize
 * an absent value (the BE serializer omits nulls) to `null`. No other normalization is applied.
 */
@Injectable({ providedIn: 'root' })
export class UserProfileFactory extends AbstractFactory<UserProfileDTO, OwnUserProfile> {
  public produce(dto: UserProfileDTO): OwnUserProfile {
    if (
      !dto ||
      !this.isNonEmptyString(dto.id) ||
      !this.isNonEmptyString(dto.firstName) ||
      !this.isNonEmptyString(dto.lastName) ||
      !this.isNonEmptyString(dto.email)
    ) {
      throw new Error('Profile response is missing a required field.');
    }
    if (!Array.isArray(dto.companies)) {
      throw new Error('Profile response is missing the companies array.');
    }
    if (
      dto.companies.some(
        company => !company || !this.isNonEmptyString(company.id) || !this.isNonEmptyString(company.name)
      )
    ) {
      throw new Error('Profile response contains an invalid company reference.');
    }
    if ([dto.address, dto.phoneNumbers, dto.notes].some(value => value != null && typeof value !== 'string')) {
      throw new Error('Profile response contains an invalid text field.');
    }

    return new OwnUserProfile({
      id: dto.id,
      companies: dto.companies.map(company => ({ id: company.id, name: company.name })),
      language: parseAccountLanguage(dto.preferences?.language),
      mergeFields: {
        firstName: dto.firstName,
        lastName: dto.lastName,
        email: dto.email,
        address: dto.address ?? null,
        phoneNumbers: dto.phoneNumbers ?? null,
        notes: dto.notes ?? null
      }
    });
  }

  private isNonEmptyString(value: unknown): value is string {
    return typeof value === 'string' && value.length > 0;
  }
}
